import "@/index.css";
import { lazy, Suspense } from "react";
import { MotionConfig } from "framer-motion";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n/I18nContext";
import { langFromPath, prefixFor } from "@/lib/langRoutes";
import { SiteProvider } from "@/context/SiteContext";
import { SmoothScroll } from "@/components/SmoothScroll";
import { Navbar } from "@/components/Navbar";
import { Footer, CookieBanner } from "@/components/Footer";
import { MiniPlayer } from "@/components/MiniPlayer";
import { DanceFloor } from "@/components/DanceFloor";
import { Applause } from "@/components/Applause";
import { ProfileSync } from "@/components/ProfileSync";
import { CanonicalSync } from "@/components/CanonicalSync";
import { VisitTracker } from "@/components/VisitTracker";
import { SocialTracker } from "@/components/SocialTracker";
import { InteractionTracker } from "@/components/InteractionTracker";
import Home from "@/pages/Home";
import AuthCallback from "@/pages/AuthCallback";

// Secondary pages are code-split: the Home bundle no longer carries Admin (charts), legal texts, etc.
const Press = lazy(() => import("@/pages/Press"));
const Parents = lazy(() => import("@/pages/Parents"));
const Admin = lazy(() => import("@/pages/Admin"));
const Legal = lazy(() => import("@/pages/Legal"));
const Login = lazy(() => import("@/pages/Login"));
const MagicLink = lazy(() => import("@/pages/MagicLink"));
const NotFound = () => <main className="min-h-screen dots-bg flex flex-col items-center justify-center px-6 text-center"><h1 className="text-4xl font-bold">404</h1><p>Pagina non trovata / Page not found</p><a href="/" className="underline mt-4">Ananawi</a></main>;
const PageFallback = () => <main className="min-h-screen dots-bg" aria-busy="true" />;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <SmoothScroll />
      <CanonicalSync />
      <VisitTracker />
      <SocialTracker />
      <InteractionTracker />
      <Navbar />
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/stampa" element={<Press />} />
        <Route path="/genitori" element={<Parents />} />
        <Route path="/login" element={<Login />} />
        <Route path="/accesso-email" element={<MagicLink />} />
        <Route path="/reimposta-password" element={<Navigate to="/login" replace />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/privacy" element={<Legal type="privacy" />} />
        <Route path="/cookie" element={<Legal type="cookie" />} />
        <Route path="/termini" element={<Legal type="terms" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <Footer />
      <MiniPlayer />
      <DanceFloor />
      <Applause />
      <CookieBanner />
      <ProfileSync />
    </>
  );
}

const preferredLanguage = () => {
  const saved = localStorage.getItem("ananawi_lang");
  if (["it", "en", "es", "pt"].includes(saved)) return saved;

  const languages = navigator.languages?.length ? navigator.languages : [navigator.language || "it"];
  for (const value of languages) {
    const code = String(value || "").slice(0, 2).toLowerCase();
    if (["it", "en", "es", "pt"].includes(code)) return code;
  }
  return "en";
};

const redirectRootToPreferredLanguage = () => {
  if (window.location.pathname !== "/") return;
  const preferred = preferredLanguage();
  const prefix = prefixFor(preferred);
  if (!prefix) return;
  window.location.replace(`${prefix}${window.location.search}${window.location.hash}`);
};

export default function App() {
  redirectRootToPreferredLanguage();
  const urlLang = langFromPath(window.location.pathname);
  return (
    <MotionConfig reducedMotion="user">
    <I18nProvider initialLang={urlLang.code}>
      <SiteProvider>
        <BrowserRouter basename={urlLang.prefix || "/"}>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </SiteProvider>
    </I18nProvider>
    </MotionConfig>
  );
}
